import React, { useContext, useEffect, useRef, useState } from "react";
import { CircularProgress, makeStyles, TextField, FormControl, Button, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions } from "@material-ui/core";

import LimitedBackdrop from "../../../components/UI/LimitedBackdrop";
import { AppContext } from "../../../contexts/AppContext";
import { separations } from "../../../constants/StyleConstants";
import { Fragment } from "react";
import PurchaseOrderExclusionCertificatesAPI from "../../../services/PurchaseOrderExclusionCertificatesAPI";
import MaterialTable from "material-table";
import { defaultGridOptions, tooltipDelay, useGridColDefs } from "../../../utils/GridUtils";
import CheckCircleOutlinedIcon from "@material-ui/icons/CheckCircleOutlined";
import CancelOutlinedIcon from "@material-ui/icons/CancelOutlined";
import ViewPortGridWrapper from "../../../containers/ViewPortGridWrapper";
import DefaultUnrederedGridMessage from "../../../containers/DefaultUnrenderedGridMessage";

const useStyles = makeStyles((theme) => ({
  ...separations,
  dialogContainer: {
    display: "flex",
    flexDirection: "column",
    minWidth: "30em",
  },
  contiguous: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gridGap: "1em",
  },
}));

const CertificateExclusionPurchaseOrdersDialog = ({
  open,
  setOpen,
  reloadGrid,
}) => {
  const classes = useStyles();
  const { showError, showWarning, showSuccess, showInfo } =
    useContext(AppContext);
  const [openBackDrop, setOpenBackDrop] = useState(false);
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState("");

  const [selectedRows, setSelectedRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [disableSelectAll, setDisableSelectAll] = useState(false);
  const [oldSearchRequest, setOldSearchRequest] = useState("");
  const [firstLoad, setFirstLoad] = useState(true);

  const resetFields = () => {
    setPurchaseOrderNumber("");
  };

  const handleSearchPurchaseOrder = (reload) => {
    const request = reload ? oldSearchRequest : purchaseOrderNumber;

    firstLoad && setFirstLoad(false);
    setOldSearchRequest(request);
    setIsLoading(true);
    setSelectedRows([]);

    PurchaseOrderExclusionCertificatesAPI.getDetail(request)
      .then((res) => {
        setRows(res.data);
      })
      .catch((error) => {
        setOpen(false);
        showError("Error al obtener el detalle de la orden de compra");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const validateSelectedRows = () => {
    let errors = [];
    const errorMessages = [];
    let i = 0;

    if ((errors[i++] = selectedRows.length === 0)) {
      errorMessages.push("Debe seleccionar al menos un item");
    }

    const hasErrors = errors.some((e) => e);
    hasErrors && showWarning(errorMessages);
    return !hasErrors;
  };

  const validateSelectedRowsAction = (value) => {
    return selectedRows.some((r) => r.state === value);
  };

  const validatePurchaseOrder = () => {
    let errors = [];
    const errorMessages = [];
    let i = 0;

    if ((errors[i++] = !purchaseOrderNumber)) {
      errorMessages.push(
        "Debe ingresar una orden de compra para poder iniciar la busqueda"
      );
    }

    const hasErrors = errors.some((e) => e);
    hasErrors && showWarning(errorMessages);
    return !hasErrors;
  };

  const validateAndSearch = () =>
    validatePurchaseOrder() && handleSearchPurchaseOrder();

  const validateAndSave = () => validateSelectedRows() && handleSave();

  useEffect(() => {
    if (rows.length > 0) {
      setDisableSelectAll(false);
    }
  }, [rows]);

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        resetFields();
        setRows([]);
        setSelectedRows([]);
        setDisableSelectAll(false);
        setFirstLoad(true);

      }, 200);
    }
  }, [open]);

  const handleSave = () => {
    setOpenBackDrop(true);
    const request = [];

    const selectedRowsNoExcluded = selectedRows.filter((r) => !r.state);

    if (selectedRowsNoExcluded.length > 0) {
      for (let i = 0; i < selectedRowsNoExcluded.length; i++) {
        const item = selectedRowsNoExcluded[i];

        const data = {
          PurchaseOrderCode: item.purchaseOrderCode,
          OcPosition: item.ocPosition,
          MaterialCode: item.materialCode,
          MaterialName: item.materialName,
        };
        request.push(data);
      }

      PurchaseOrderExclusionCertificatesAPI.insertExcludedCertificate(request)
        .then((res) => {
          handleSearchPurchaseOrder(true);
          showSuccess("Los items seleccionados se han excluido exitosamente");
        })
        .catch((error) => {
          setOpen(false);
          showError("Error al excluir los items");
        })
        .finally(() => {
          setIsLoading(false);
          setOpenBackDrop(false);
        });
    } else {
      showInfo("Los items seleccionados ya se encuentran excluidos");
      setIsLoading(false);
      setOpenBackDrop(false);
    }
  };

  const handleDeleteExclusion = () => {
    setOpenBackDrop(true);
    const request = [];

    const selectedRowsExcluded = selectedRows.filter((r) => r.state);

    if (selectedRowsExcluded.length > 0) {
      for (let i = 0; i < selectedRowsExcluded.length; i++) {
        const item = selectedRowsExcluded[i];

        const data = {
          Id: item.id,
          PurchaseOrderCode: item.purchaseOrderCode,
          OcPosition: item.ocPosition,
          MaterialCode: item.materialCode,
          MaterialName: item.materialName,
        };
        request.push(data);
      }

      PurchaseOrderExclusionCertificatesAPI.deleteExcludedCertificate(request)
        .then((res) => {
          handleSearchPurchaseOrder(true);
          showSuccess("Las exclusiones se han eliminado exitosamente");
        })
        .catch((error) => {
          setOpen(false);
          showError("Error al eliminar exclusión de los items");
        })
        .finally(() => {
          setIsLoading(false);
          setOpenBackDrop(false);
        });
    } else {
      showInfo("Los items seleccionados no se encuentran excluidos");
      setIsLoading(false);
      setOpenBackDrop(false);
    }
  };

  const tableRef = useRef();

  const BoldValue = ({ value }) => {
    return <b>{value}</b>;
  };

  const colDefs = useGridColDefs([
    { title: "Posición OC", field: "ocPosition" },
    { title: "Código Material", field: "materialCode", valueRender: (value) => value.replace(/^0+/, "") },
    {
      title: "Nombre Material",
      field: "materialName",
      valueRender: (value, rowData) => <BoldValue value={value} />,
    },

    {
      title: "Excluido",
      field: "state",
      headerStyle: { textAlign: "center" },
      render: (rowData) =>
        rowData.state ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <BoldValue value={"SI"} />

            <CheckCircleOutlinedIcon
              color="primary"
              style={{ verticalAlign: "middle", marginLeft: "15px" }}
            />
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <BoldValue value={"NO"} />

            <CancelOutlinedIcon
              color="secondary"
              style={{ verticalAlign: "middle", marginLeft: "15px" }}
            />
          </div>
        ),
    },
  ]);

  return (
  
      <Dialog
        maxWidth="md"
        fullWidth={true}
        onClose={() => setOpen(false)}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <DialogTitle
          id="customized-dialog-title"
          onClose={() => setOpen(false)}
        >
          Excluir órden de compra del requerimiento de certificado
        </DialogTitle>
        <DialogContent dividers>
        <LimitedBackdrop open={openBackDrop}>
          <CircularProgress color="primary" />
        </LimitedBackdrop>
        <div>
          <Fragment>
            <div>
              <TextField
                margin="dense"
                id="nroPedidoCompra"
                style={{ width: 200 }}
                label="Nro. de pedido de compra"
                onChange={(e) => setPurchaseOrderNumber(e.target.value)}
                value={purchaseOrderNumber}
              />

              <Button
                variant="outlined"
                color="primary"
                onClick={validateAndSearch}
                style={{ margin: "15px 0 0 30px" }}
              >
                Buscar
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={resetFields}
                style={{ margin: "15px 0 0 10px" }}
              >
                Limpiar
              </Button>
            </div>

            <div className={classes.root}>
              <div
                className={classes.gridButtonsWrapper}
                style={{ marginTop: "10px" }}
              >
                <Button
                  style={{ marginRight: "10px" }}
                  disabled={selectedRows.isEmpty()}
                  variant="contained"
                  color="secondary"
                  size="small"
                  startIcon={<CancelOutlinedIcon />}
                  onClick={validateSelectedRows && handleDeleteExclusion}
                >
                  Remover exclusión
                </Button>

                <Button
                  disabled={selectedRows.isEmpty()}
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<CheckCircleOutlinedIcon />}
                  onClick={validateAndSave}
                >
                  Excluir
                </Button>
              </div>

              <ViewPortGridWrapper fullResponsive={true}>
                <DefaultUnrederedGridMessage render={!firstLoad}>
                  <MaterialTable
                    {...defaultGridOptions}
                    options={{
                      ...defaultGridOptions.options,
                      pageSizeOptions: [
                        ...defaultGridOptions.options.pageSizeOptions,
                        defaultGridOptions.options.pageSizeOptions.last() * 2,
                      ],
                      selection: true,
                      headerSelectionProps: {
                        color: "primary",
                        disabled: disableSelectAll,
                      },
                      selectionProps: (rowData) => {
                        return {
                          color: "primary",
                        };
                      },
                    }}
                    onSelectionChange={(rows, row) => setSelectedRows(rows)}
                    columns={colDefs}
                    data={rows}
                    isLoading={isLoading}
                    tableRef={tableRef}
                  />
                </DefaultUnrederedGridMessage>
              </ViewPortGridWrapper>
            </div>
          </Fragment>
        </div>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setOpen(false)} color="secondary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    
  );
};
export default CertificateExclusionPurchaseOrdersDialog;
